import { isEmptyValue, formatDateOnlyString } from "../../utils/propertyMapping";

const KV_TO_V = 1000;

// 68.86 -> "68,86 ha"
function formatHectares(value: any): string {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return `${number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ha`;
}

// Fonte só informa mês/ano; o banco guarda o dia 1. "2023-04-01Z" -> "04/2023"
function formatMonthYear(value: any): string {
  const [year, month] = String(value).split("T")[0].split("-");
  if (!year || !month) return String(value);
  return `${month}/${year}`;
}

export interface PropertyMapping {
  label: string; // Portuguese label to display
  order?: number; // Display order (lower numbers first)
  format?: (value: any) => string; // Optional value formatter
  allowEmpty?: boolean; // Display even when value is empty (format must supply a fallback)
}

export type LayerPropertyMappings = Record<string, PropertyMapping>;

// Property mappings for each layer
export const propertyMappings: Record<string, LayerPropertyMappings> = {
  // Unidades de Conservação Ambiental
  // Known properties from console log: fid, objectid, nome_uc1, categori3, grupo4
  unidades_conservacao_ambiental: {
    nome_uc1: { label: "Nome da Unidade", order: 1 },
    categori3: { label: "Categoria", order: 2 },
    grupo4: { label: "Grupo", order: 3 },
    ano_cria6: { label: "Ano de Criação", order: 4 },
    ato_lega9: { label: "Ato Legal", order: 5 },
    esfera5: { label: "Esfera", order: 6 },
    nome_org12: { label: "Órgão Responsável", order: 7 },

    // Add more properties here as they are discovered
  },

  // Aves Migratórias (Áreas de concentração)
  // Properties to be discovered - single symbol style, no property-based rules
  aves_migratorias_areas_de_concentracao: {
    nome: {
      label: "Nome",
      order: 1,
      format: (value: string) => {
        if (isEmptyValue(value)) {
          return "Área de Concentração de Aves Migratórias";
        }
        // Remove "PE_" prefix and replace underscores with spaces
        return value.replace(/^PE_/, "").replace(/_/g, " ");
      },
    },
    criterio: { label: "Critério", order: 2 },
    // Add properties here after checking console logs
  },

  // Rios
  // Single symbol style - properties to be discovered
  rios: {
    noriocomp: { label: "Nome do Rio", order: 1 },
    // Add properties here after checking console logs
  },

  // Canais
  // Known from SLD: adt_nm_adu, adt_status
  canais: {
    adt_nm_adu: { label: "Nome do Canal", order: 1 },
    adt_status: { label: "Status", order: 2 },
    // Add more properties here as they are discovered
  },

  // Adutoras
  // Known from SLD: adt_status, adt_tipo, adt_nm_adu
  adutoras: {
    adt_nm_adu: { label: "Nome da Adutora", order: 1 },
    adt_status: { label: "Status", order: 2 },
    adt_tipo: { label: "Tipo de Infraestrutura", order: 3 },
    // Add more properties here as they are discovered
  },

  // Corpos d'água
  corpos_dagua: {
    nmoriginal: { label: "Nome do Corpo d'Água", order: 1 },
    detipomass: { label: "Tipo", order: 2 },
  },

  // Municípios PE
  municipios_pe: {
    nm_mun: { label: "Município", order: 1 },
  },

  apl_pe_observatorio: {
    apls: { label: "Arranjo Produtivo Local", order: 1 },
    nm_mun: { label: "Município", order: 2 },
  },

  // Vocações Econômicas (municipios_pe_adepe) não entra aqui: o tooltip dela
  // é montado por generateVocacaoTooltipHTML a partir de utils/vocacoes.

  regioes_imediatas_pe: {
    mesorregiao: { label: "Mesorregião", order: 1 },
    nm_rgi: { label: "Região Imediata", order: 2 },
    area_km2: {
      label: "Área (km²)",
      order: 3,
      format: (value: number) =>
        value
          ? value.toLocaleString("pt-BR", { maximumFractionDigits: 2 })
          : "",
    },
    nm_rgint: { label: "Região Intermediária", order: 4 },
    cd_rgi: { label: "Código Região Imediata", order: 5 },
    cd_mesorregiao: { label: "Código Mesorregião", order: 6 },
    cd_rgint: { label: "Código Região Intermediária", order: 7 },
    nm_uf: { label: "Estado", order: 8 },
  },

  // Estado PE
  estado_pe: {
    nm_estado: { label: "Estado", order: 1 },
    sigla: { label: "Sigla", order: 2 },
  },

  // Estados Brasil
  estados_bra: {
    nm_estado: { label: "Estado", order: 1 },
    sigla: { label: "Sigla", order: 2 },
  },

  // Linhas de Transmissão - Base Existente
  linhas_de_transmissao_base_existente: {
    nomelongo: { label: "Nome da Linha", order: 1 },
    nomerede: { label: "Tipo", order: 2 },
    tensao: { label: "Tensão (kV)", order: 3 },
  },

  // Linhas de Transmissão - Expansão Planejada
  linhas_de_transmissao_expansao_planejada: {
    nomelongo: { label: "Nome da Linha", order: 1 },
    nomerede: { label: "Tipo", order: 2 },
    tensao: { label: "Tensão (kV)", order: 3 },
    ano_planej: { label: "Previsão de Operação", order: 4 },
  },

  // Subestações - Base Existente
  subestacoes_base_existente: {
    nome: { label: "Nome da Subestação", order: 1 },
    concession: { label: "Concessionária", order: 2 },
    tensao: { label: "Tensão (kV)", order: 3 },
    ano_opera: { label: "Ano", order: 4 },
  },

  // Subestações - Expansão Planejada
  // Properties to be discovered
  subestacoes_expansao_planejada: {
    nome: { label: "Nome da Subestação", order: 1 },
    concession: { label: "Concessionária", order: 2 },
    tensao: { label: "Tensão (kV)", order: 3 },
    ano_opera: { label: "Ano de Operação", order: 4 },
  },

  // Parques Eólicos - Base Existente
  eol_base_existente: {
    nome: { label: "Nome do Parque Eólico", order: 1 },
    potencia: { label: "Potência (kW)", order: 2 },
    ini_oper: { label: "Início de Operação", order: 3 },
    propriet: { label: "Proprietário", order: 4 },
  },

  // Parques Eólicos - Expansão Planejada
  eol_expansao_planejada: {
    nome: { label: "Nome do Parque Eólico", order: 1 },
    p_out_kw: { label: "Potência (kW)", order: 2 },
    ini_oper: { label: "Início de Operação", order: 3 },
    propriet: { label: "Proprietário", order: 4 },
  },

  ufv_base_existente: {
    nome: { label: "Nome da UFV", order: 1 },
    potencia: { label: "Potência (kW)", order: 2 },
    propriet: { label: "Proprietário", order: 3 },
    ini_oper: { label: "Data", order: 4 },
  },

  ufv_expansao_planejada: {
    nome: { label: "Nome da UFV", order: 1 },
    potencia: { label: "Potência (kW)", order: 2 },
    leilao: { label: "Início de Operação", order: 3 },
    ano_prev: { label: "Ano previsto", order: 4 },
  },

  // Usinas Hidrelétricas Existentes
  uhe_base_existente: {
    nome: { label: "Nome", order: 0 },
    propriet: { label: "Nome da Usina", order: 1 },
    rio: { label: "Proprietário", order: 2 },
    ini_opera: { label: "Início de Operação", order: 3 },
    proprietar: { label: "Proprietário", order: 4 },
    pot_kw: { label: "Potência (kW)", order: 5 },
  },

  // Pequenas Centrais Hidrelétricas
  pch_base_existente: {
    propriet: { label: "Nome da Usina", order: 1 },
    rio: { label: "Proprietário", order: 2 },
    ini_opera: { label: "Início de Operação", order: 3 },
  },

  // Centrais Geradoras Hidrelétricas
  // Properties to be discovered
  cgh_base_existente: {
    propriet: { label: "Nome da Usina", order: 1 },
    rio: { label: "Proprietário", order: 2 },
    ini_opera: { label: "Início de Operação", order: 3 },
  },

  // Usinas de Biomassa
  ute_biomassa_base_existente: {
    propriet: { label: "Nome da Usina", order: 1 },
    ceg: { label: "Matéria prima", order: 2 },
  },

  // Usinas Termelétricas Fóssil
  ute_fossil_base_existente: {
    propriet: { label: "Nome da Usina", order: 1 },
    ceg: { label: "Combustível", order: 2 },
  },

  // Rodovias Federais
  rodovias_federais: {
    codigo_br: {
      label: "Código BR",
      order: 1,
      format: (value: any) => `BR-${value}`,
    },
    local_inicio: { label: "Local Início", order: 2 },
    local_fim: { label: "Local Fim", order: 3 },
    administra: { label: "Administração", order: 4 },
  },

  // Rodovias Estaduais
  rodovias_estaduais: {
    codigo_rod: {
      label: "Código PE",
      order: 1,
      format: (value: any) => `PE-${value}`,
    },
    local_inic: { label: "Local Início", order: 2 },
    local_fim: { label: "Local Fim", order: 3 },
    jurisdicao: { label: "Administração", order: 4 },
  },

  // Ferrovias
  ferrovias: {
    nome: {
      label: "Nome da Ferrovia",
      order: 1,
      allowEmpty: true,
      format: (value: any) =>
        isEmptyValue(value) ? "Ferrovia planejada" : String(value),
    },
    tip_situac: { label: "Situação", order: 2 },
    bitola: { label: "Bitola", order: 3 },
    sigla: { label: "Sigla", order: 4 },
    municipio: { label: "Município", order: 5 },
    uf: { label: "UF", order: 6 },
    extensao: { label: "Extensão Total (km)", order: 7 },
    sigla_coin: { label: "Sigla Coincidente", order: 8 },
    extensao_c: { label: "Extensão Coincidente (km)", order: 9 },
    extensao_e: { label: "Extensão Estadual (km)", order: 10 },
  },

  // Transnordestina - Trecho Ferroviário (VALEC)
  "TRANSNORDESTINA-TRECHO-FERROVIÁRIO-VALEC-PE": {
    empreendim: { label: "Empreendimento", order: 1 },
    sigla: { label: "Sigla", order: 2 },
    trecho: { label: "Trecho", order: 3 },
    situacao: { label: "Situação", order: 4 },
    ext: {
      label: "Extensão (km)",
      order: 5,
      format: (value: any) => String(value).replace(".", ","),
    },
    bitola: { label: "Bitola", order: 6 },
    concession: { label: "Concessionária", order: 7 },
    subconcess: { label: "Subconcessionária", order: 8 },
    label: { label: "Identificação", order: 9 },
    participac: { label: "Participação", order: 10 },
  },

  cavernas_pe: {
    caverna: { label: "Nome", order: 1 },
    municipio: { label: "Município", order: 2 },
    localidade: { label: "Localidade", order: 3 },
    registro_n: { label: "Registro", order: 4 },
    altitude: { label: "Altitude (m)", order: 5 },
  },
  assentamentos_publicos: {
    nome_proje: { label: "Nome do Assentamento", order: 1 },
    forma_obte: { label: "Forma de Obtenção", order: 2 },
    data_obten: { label: "Data de Obtenção", order: 3 },
  },
  area_quilombola: {
    nm_comunid: { label: "Nome da Comunidade", order: 1 },
    fase: { label: "Fase", order: 2 },
    responsave: { label: "Responsável", order: 3 },
  },
  terras_indigenas_homologadas: {
    terrai_nom: { label: "Nome da Terra Indígena", order: 1 },
    etnia_nom: { label: "Etnia", order: 2 },
    modalidade: { label: "Modalidade", order: 3 },
    undadm_nom: { label: "Unidade Administrativa", order: 4 },
  },
  sitios_arqueologicos: {
    identifica: { label: "Identificação", order: 1 },
    ds_naturez: { label: "Natureza", order: 2 },
    ds_classif: { label: "Classificação", order: 3 },
    ds_tipo_be: { label: "Tipo", order: 4 },
    co_iphan: { label: "Código IPHAN", order: 5 },
    sintese_be: { label: "Síntese", order: 6 },
    dt_cadastr: { label: "Data de Cadastro", order: 7 },
  },
  areas_prioritarias_conservacao_caatinga: {
    nome_area: { label: "Nome da Área", order: 1 },
    importbio_: { label: "Importância biológica", order: 2 },
    prioridade: { label: "Prioridade de Preservação", order: 3 },
  },
  areas_prioritarias_conservacao_mata_atlantica: {
    nome_area: { label: "Nome da Área", order: 1 },
    importbio_: { label: "Importância biológica", order: 2 },
    prioridade: { label: "Prioridade de Preservação", order: 3 },
  },
  pe_aeroportos: {
    name: { label: "Nome do Aeroporto", order: 1 },
    type: {
      label: "Porte",
      order: 2,
      format: (value: string) => {
        if (!value) return "";
        // Translation map for airport types
        const translations: Record<string, string> = {
          small_airport: "Pequeno",
          medium_airport: "Médio",
          large_airport: "Grande",
        };
        return (
          translations[value] ||
          value.replace(/_airport$/, "").replace(/_/g, " ")
        );
      },
    },
    municipali: { label: "Município", order: 3 },
    gps_code: { label: "Código GPS", order: 4 },
  },
  portos: {
    nome: { label: "Nome do Porto", order: 1 },
    situacao: { label: "Situação", order: 2 },
    tipo: { label: "Tipo", order: 3 },
    companhia: { label: "Companhia", order: 4 },
  },
  polos_empresariais: {
    municipio: { label: "Município", order: 1 },
    // Polos sem tipologia trazem o quadro geral de áreas no lugar (null aqui)
    tipologias_industriais: { label: "Tipologias Industriais", order: 2 },
    polo_empresarial: { label: "Polo Empresarial", order: 3 },
    regiao_desenvolvimento: {
      label: "Região de desenvolvimento",
      order: 4,
    },
    area_gleba_ha: { label: "Gleba", order: 5, format: formatHectares },
    area_lotes_ha: { label: "Lotes", order: 6, format: formatHectares },
    area_livre_ha: { label: "Área livre", order: 7, format: formatHectares },
    area_remanescente_ha: {
      label: "Área remanescente",
      order: 8,
      format: formatHectares,
    },
    faixa_dominio_ha: {
      label: "Faixa de domínio",
      order: 9,
      format: formatHectares,
    },
    sistema_viario_ha: {
      label: "Sistema viário",
      order: 10,
      format: formatHectares,
    },
    viario_a_construir_ha: {
      label: "Viário a construir",
      order: 11,
      format: formatHectares,
    },
    area_verde_ha: { label: "Área verde", order: 12, format: formatHectares },
    qtd_glebas: { label: "Qtd. de glebas", order: 13 },
    qtd_lotes: { label: "Qtd. de lotes", order: 14 },
    quadro_areas: { label: "Quadro de áreas", order: 15 },
    data_atualizacao: {
      label: "Atualização",
      order: 16,
      format: formatMonthYear,
    },
    distancia_capital: { label: "Distância para a capital", order: 17 },
    distancia_suape: { label: "Distância para Suape", order: 18 },
    distancia_aeroporto_recife: {
      label: "Distância aeroporto",
      order: 19,
    },
  },
  // LTS Neoenergia - Rede de Distribuição AT
  "LTS-neoenergia": {
    nome: { label: "Nome", order: 1 },
    codigo: { label: "Código", order: 2 },
    orig_dest: { label: "Origem / Destino", order: 3 },
    tensao_kv: { label: "Tensão (kV)", order: 4 },
    tensao_inf: { label: "Tensão Inferior (kV)", order: 5 },
  },
  neoenergia_pe_uncrmt: {
    cod_id: { label: "Código ID", order: 1 },
    pot_nom: {
      label: "Potência Nominal",
      order: 3,
      format: (value: string) => `${value} kVA`,
    },
    sub: { label: "Subestação", order: 4 },
    ctmt: { label: "Circuito MT", order: 5 },
    sit_ativ: {
      label: "Situação",
      order: 6,
      format: (value: string) => (value === "AT" ? "Ativa" : value),
    },
    dat_con: { label: "Data de Conexão", order: 7 },
  },
  neoenergia_pe_unremt: {
    COD_ID: { label: "Código ID", order: 1 },
    SUB: { label: "Subestação", order: 3 },
    SIT_ATIV: {
      label: "Situação",
      order: 5,
      format: (value: string) => (value === "AT" ? "Ativa" : value),
    },
  },
  neoenergia_pe_unseat: {
    cod_id: { label: "Código ID", order: 1 },
    sit_ativ: {
      label: "Situação",
      order: 5,
      format: (value: string) => (value === "AT" ? "Ativa" : value),
    },
  },
  neoenergia_pe_uncrat: {
    cod_id: { label: "Código ID", order: 1 },
    pot_nom: {
      label: "Potência Nominal",
      order: 3,
      format: (value: string) => `${value} kVA`,
    },
    sub: { label: "Subestação", order: 4 },
    sit_ativ: {
      label: "Situação",
      order: 6,
      format: (value: string) => (value === "AT" ? "Ativa" : value),
    },
    dat_con: { label: "Data de Conexão", order: 7 },
  },
  neoenergia_pe_unreat: {
    cod_id: { label: "Código ID", order: 1 },
    sub: { label: "Subestação", order: 3 },
    sit_ativ: {
      label: "Situação",
      order: 5,
      format: (value: string) => (value === "AT" ? "Ativa" : value),
    },
  },
  neoenergia_pe_untrat: {
    // Cabeçalho do tooltip: "PQR: Pesqueira" (sigla + município onde fica,
    // calculado na view do geo-scripts/subestacoes_disponibilidade). Substitui
    // a linha "Sub", que só repetia a sigla.
    title_name: { label: "Subestação", order: 1 },
    tensao: { label: "Tensão", order: 2 },
    pot_nom: {
      label: "Potência Nominal",
      order: 3,
      format: (value: string) => `${value} kVA`,
    },
    // Unidade assumida como MW (não confirmada pela planilha de origem) por
    // acompanhar as faixas de disponibilidade, que são expressas em MW.
    pot_maxima: {
      label: "Potência Máxima",
      order: 4,
      format: (value: string) => `${value} MW`,
    },
    // Rótulo "Faixa de Disponibilidade", não só "Faixa": a palavra sozinha já
    // é usada no app para outras duas coisas (faixa de valor de pixel de
    // raster, faixa de velocidade do vento no gráfico eólico).
    faixa: { label: "Faixa de Disponibilidade", order: 5 },
    // Algumas subestações (ex.: AFI) têm um segundo nível de tensão com
    // disponibilidade própria (ver subestacoes_disponibilidade no
    // geo-scripts) -- nulo na maioria das linhas, então some do popup nelas.
    tensao_2: { label: "Tensão 2", order: 6 },
    pot_maxima_2: {
      label: "Potência Máxima 2",
      order: 7,
      format: (value: string) => `${value} MW`,
    },
    faixa_2: { label: "Faixa de Disponibilidade 2", order: 8 },
    sit_ativ: {
      label: "Situação",
      order: 9,
      format: (value: string) => (value === "AT" ? "Ativa" : value),
    },
    dat_con: { label: "Data de Conexão", order: 10 },
    // Valor fixo injetado via staticTooltipProperties (não vem do GeoServer).
    ultima_atualizacao_disponibilidade: {
      label: "Disponibilidade atualizada em",
      order: 11,
    },
  },
  neoenergia_pe_ssdmt: {
    cod_id: { label: "Código ID", order: 1 },
    tip_inst: {
      label: "Tipo de Instalação",
      order: 2,
      format: (value: string) => {
        const tipos: Record<string, string> = {
          LD_AER_MT: "Linha de Distribuição Aérea MT",
          LD_SUB_MT: "Linha de Distribuição Subterrânea MT",
        };
        return tipos[value] || value.replace(/_/g, " ");
      },
    },
    ctmt: { label: "Circuito MT", order: 3 },
    fas_con: { label: "Fases", order: 5 },
    sitcont: { label: "Situação", order: 6 },
  },
  neoenergia_pe_untrmt: {
    cod_id: { label: "Código ID", order: 1 },
    pot_nom: {
      label: "Potência Nominal",
      order: 3,
      format: (value: number) => `${value} kVA`,
    },
    sub: { label: "Subestação", order: 4 },
    ctmt: { label: "Circuito MT", order: 5 },
    sit_ativ: {
      label: "Situação",
      order: 6,
      format: (value: string) => (value === "AT" ? "Ativa" : value),
    },
    ten_lin_se: {
      label: "Tensão Secundária",
      order: 7,
      format: (value: number) => `${value * KV_TO_V} V`,
    },
    dat_con: { label: "Data de Conexão", order: 8 },
    fas_con_p: { label: "Fase Primária", order: 9 },
    fas_con_s: { label: "Fase Secundária", order: 10 },
  },
  gasoduto_transporte_pe: {
    nome_dut_1: { label: "Nome do Gasoduto", order: 1 },
    munic_orig: { label: "Município de Origem", order: 2 },
    munic_dest: { label: "Município de Destino", order: 3 },
    fontes: { label: "Fontes", order: 4 },
  },
  gasoduto_distribuicao_pe: {
    name: { label: "Nome do Gasoduto", order: 1 },
    fonte: { label: "Fonte", order: 2 },
    distrib: { label: "Distribuidora", order: 3 },
  },
  gnv_stations: {
    posto_nome_app: { label: "Nome do posto", order: 1 },
    posto_endereco: { label: "Endereço", order: 2 },
    posto_ativo: {
      label: "Status",
      order: 3,
      format: (value: any) => (String(value) === "1" ? "Ativo" : "Inativo"),
    },
  },
  hidrovias_fluxo_de_cabotagem: {
    nome: { label: "Nome da Hidrovia", order: 1 },
    tipo: { label: "Tipo", order: 2 },
    navegacao: { label: "Navegação", order: 3 },
  },
  zoneamento_agroecologico_pernambuco: {
    simb_leg: { label: "Classe de Zoneamento", order: 1 },
  },
  "estacoes-de-trem-do-grande-recife": {
    name: { label: "Nome da Estação", order: 1 },
  },
  "linhas-de-trem-do-grande-recife": {
    name: { label: "Linha", order: 1 },
  },
  "telecom-fibra-pe": {
    name: { label: "Nome", order: 1 },
  },
  "telecom-torres-pe": {
    name: { label: "Nome", order: 1 },
  },
  // Conjuntos elétricos: o tooltip mostra só o resumo do ano de referência;
  // a série histórica completa aparece no gráfico aberto ao clicar.
  neoenergia_pe_conj_dgc: {
    NOME: { label: "Conjunto", order: 1 },
    DISTRIBUIDORA: { label: "Distribuidora", order: 2 },
    DGC_2025: {
      label: "DGC (2025)",
      order: 3,
      allowEmpty: true,
      format: (value: any) =>
        isEmptyValue(value) ? "sem dados" : Number(value).toFixed(3),
    },
    DEC_APU_2025: {
      label: "DEC apurado (2025)",
      order: 4,
      format: (value: any) => `${Number(value).toFixed(2)} h`,
    },
    DEC_LIM_2025: {
      label: "DEC limite (2025)",
      order: 5,
      format: (value: any) => `${Number(value).toFixed(2)} h`,
    },
    FEC_APU_2025: {
      label: "FEC apurado (2025)",
      order: 6,
      format: (value: any) => `${Number(value).toFixed(2)} interrupções`,
    },
    FEC_LIM_2025: {
      label: "FEC limite (2025)",
      order: 7,
      format: (value: any) => `${Number(value).toFixed(2)} interrupções`,
    },
  },

  // Ocorrências de Recursos Minerais — SGB/CPRM
  // Feição achata dados de "afloramento" (ponto de observação geológica) e
  // "ocorrência mineral" (achado avaliado economicamente) — ver CONTEXT.md.
  sgb_ocorrencias_recursos_minerais: {
    substancias: { label: "Substâncias", order: 0 },
    toponimia: { label: "Localidade", order: 1 },
    classes_utilitarias: { label: "Classe de Utilização", order: 2 },
    status_economico: { label: "Status Econômico", order: 3 },
    importancia: { label: "Importância", order: 4 },
    municipio: { label: "Município", order: 5 },
    provincia: { label: "Província Geológica", order: 6 },
    tipo_afloramento: { label: "Tipo de Afloramento", order: 7 },
    descricao: { label: "Descrição", order: 8 },
    rochas: { label: "Rochas", order: 9 },
    rochas_hospedeiras: { label: "Rochas Hospedeiras", order: 10 },
    rochas_encaixantes: { label: "Rochas Encaixantes", order: 11 },
    localizacao_mina: { label: "Localização da Mina", order: 12 },
    situacao_mina: { label: "Situação da Mina", order: 13 },
    motivo_inatividade_mina: {
      label: "Motivo de Inatividade (Mina)",
      order: 14,
    },
    situacao_garimpo: { label: "Situação do Garimpo", order: 15 },
    motivo_inatividade_garimpo: {
      label: "Motivo de Inatividade (Garimpo)",
      order: 16,
    },
    morfologia: { label: "Morfologia", order: 17 },
    texturas: { label: "Texturas", order: 18 },
    tipos_alteracao: { label: "Tipo de Alteração", order: 19 },
    geologo: { label: "Geólogo Responsável", order: 20 },
    data_cadastro: {
      label: "Data de Cadastro",
      order: 21,
      format: formatDateOnlyString,
    },
  },
};

// Layers where tooltip is suppressed when any of the listed fields is null/empty
export const tooltipConditions: Record<string, { requiredFields: string[] }> = {
  apl_pe_observatorio: { requiredFields: ["apls"] },
};

// Fixed values appended to every feature's tooltip for the layer (not present
// in the GeoServer data). Each key still needs an entry in propertyMappings.
export const staticTooltipProperties: Record<string, Record<string, string>> = {
  neoenergia_pe_untrat: { ultima_atualizacao_disponibilidade: "14/09/2026" },
};

// Properties that should always be excluded from tooltips
export const excludedProperties = [
  "fid",
  "objectid",
  "id",
  "gid",
  "geom",
  "geometry",
  "shape",
  "shape_length",
  "shape_area",
  "globalid",
  "created_user",
  "created_date",
  "last_edited_user",
  "last_edited_date",
];
